import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

// Briefs change at ingest pace, not per second: refresh every 5 minutes, and
// only while the tab is visible. Coming back to a hidden tab refreshes at
// once if the data is older than one interval.
export const POLL_MS = 5 * 60_000;

/**
 * One dashboard data source with its own loading / error / last-good state.
 *
 *   const attacks = useResource(() => apiGet('/dashboard/attacks', params), [days], { enabled });
 *   attacks = { data, error, loading, updatedAt, reload }
 *
 * - data is kept when a refresh fails (shown as stale next to the error)
 * - a 401 signs the user out and sends them to /login
 * - `enabled: false` defers the first request (tabs load lazily)
 * - responses that arrive out of order (window changed mid-request) are dropped
 */
export default function useResource(fetcher, deps = [], { enabled = true, poll = true } = {}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [state, setState] = useState({ data: null, error: null, loading: enabled, updatedAt: 0 });
  const seq = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // navigate changes identity on every URL change (tab switches write ?tab=),
  // so keep it and logout in refs to keep `load` stable.
  const signOutRef = useRef(null);
  signOutRef.current = () => {
    logout();
    navigate('/login', { replace: true });
  };
  const updatedRef = useRef(0);
  // Data fetched for other inputs (e.g. the 30-day window while 7 days
  // loads) is never handed out under the new inputs' label.
  const key = JSON.stringify(deps);
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(async () => {
    const id = ++seq.current;
    const forKey = keyRef.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await fetcherRef.current();
      if (id !== seq.current) return;
      updatedRef.current = Date.now();
      setState({ data, key: forKey, error: null, loading: false, updatedAt: updatedRef.current });
    } catch (error) {
      if (id !== seq.current) return;
      if (error?.response?.status === 401) {
        signOutRef.current();
        return;
      }
      setState((s) => ({ ...s, error, loading: false }));
    }
  }, []);

  // (Re)load when the inputs change.
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return () => { seq.current += 1; };
  }, [enabled, load, key]);

  useEffect(() => {
    if (!enabled || !poll) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') load();
    };
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - updatedRef.current > POLL_MS) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, poll, load]);

  const current = state.key === key;
  return {
    data: current ? state.data : null,
    error: state.error,
    loading: state.loading || (enabled && !current && !state.error),
    updatedAt: current ? state.updatedAt : 0,
    reload: load,
  };
}

/** Re-render every `ms` so "Updated 3m ago" labels stay true. */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
